// Servicio de "Solicitar cambio": POST /solicitudes-cambio (multipart).
// Mismo patron que usuariosApi.js: axios.create (ya parchado con los
// interceptores de sesion de redux/axiosSetup.js) + token de localStorage.
import axios from "axios";
import { PATH } from "../../redux/utils";

const api = axios.create({ baseURL: PATH });

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Unico lugar donde se traducen los valores del formulario a los ENUM del backend.
const TIPO_API = { error: "error", visual: "cambio_visual", funcion: "nueva_funcion", otro: "otro" };
const PRIORIDAD_API = { urgente: "urgente", normal: "normal", baja: "cuando_se_pueda" };

export const solicitudesCambioService = {
  /**
   * payload: {
   *   titulo, tipo, descripcion, prioridad,
   *   imagenAnotada: Blob, capturaOriginal: Blob,   // obligatorias
   *   imagenesAdicionales: File[],                   // max 5
   *   infoTecnica: { ruta, tituloVista, pestanaActiva, resolucionCorta, versionApp, ... },
   * }
   * Devuelve { id, folio, estado, created_at }. Rechaza con el error de axios.
   */
  async enviar(payload) {
    const info = payload.infoTecnica || {};
    const fd = new FormData();
    fd.append("titulo", payload.titulo);
    fd.append("tipo", TIPO_API[payload.tipo] || payload.tipo);
    fd.append("descripcion", payload.descripcion);
    fd.append("prioridad", PRIORIDAD_API[payload.prioridad] || payload.prioridad);
    fd.append("ruta", info.ruta || "");
    fd.append("vista", info.tituloVista || "");
    fd.append("pestana", info.pestanaActiva || "");
    fd.append("resolucion", info.resolucionCorta || "");
    fd.append("version_app", info.versionApp || "");
    // usuario y rol NO se envian: el backend los toma del token.
    fd.append("captura_original", payload.capturaOriginal, "captura_original.png");
    fd.append("captura_anotada", payload.imagenAnotada, "captura_anotada.png");
    (payload.imagenesAdicionales || []).forEach((f) => fd.append("adicionales", f, f.name));

    // Sin Content-Type manual: axios/navegador agregan el boundary del multipart.
    const { data } = await api.post("/solicitudes-cambio", fd, { headers: authHeader() });
    return data;
  },

  // Modulo "Programador": todas las solicitudes, de cualquier usuario (requiere administración).
  async listarTodas({ limit = 20, offset = 0, estado } = {}) {
    const { data } = await api.get("/solicitudes-cambio", {
      headers: authHeader(),
      params: { limit, offset, ...(estado ? { estado } : {}) },
    });
    return data;
  },

  // Modulo "Mis solicitudes": solo las del usuario en sesion.
  async listarMias({ limit = 20, offset = 0 } = {}) {
    const { data } = await api.get("/solicitudes-cambio/mias", { headers: authHeader(), params: { limit, offset } });
    return data;
  },

  async obtenerDetalle(id) {
    const { data } = await api.get(`/solicitudes-cambio/${id}`, { headers: authHeader() });
    return data;
  },

  urlArchivo(idSolicitud, idArchivo) {
    return `${PATH}/solicitudes-cambio/${idSolicitud}/archivos/${idArchivo}`;
  },

  // Administración: envía el monto y la nota de la cotización al cliente.
  async enviarCotizacion(id, { monto, nota }) {
    const { data } = await api.put(`/solicitudes-cambio/${id}/cotizacion`, { monto, nota }, { headers: authHeader() });
    return data;
  },

  // Cliente: acepta o declina la cotización de su propia solicitud.
  async responderCotizacion(id, acepta) {
    const { data } = await api.put(`/solicitudes-cambio/${id}/cotizacion-respuesta`, { acepta }, { headers: authHeader() });
    return data;
  },

  authHeader,
};

// Mensaje claro para mostrar en el modal segun el error del envio.
export function mensajeErrorEnvio(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const detalleTexto = typeof detail === "string" ? detail : "";

  if (!err?.response) return "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.";
  if (status === 401) return "Tu sesión expiró. Vuelve a iniciar sesión; lo que escribiste sigue aquí.";
  if (status === 403) return "No tienes permiso para enviar solicitudes de cambio. Pide a un administrador que te lo active.";
  if (status === 413) return detalleTexto || "Una de las imágenes pesa más de 10 MB. Quítala o reemplázala e inténtalo de nuevo.";
  if (status === 422) return detalleTexto || "Hay datos que el servidor no aceptó. Revisa el formulario e inténtalo de nuevo.";
  return detalleTexto || "No se pudo enviar la solicitud. Inténtalo de nuevo.";
}
